// frontend/src/components/PhotoModal.jsx - Handle presigned URLs

import React, { useState, useEffect } from 'react';
import { XIcon, CameraIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { showSuccess, showError } from '../utils/toastUtils';
import api from '../services/api';

const PhotoModal = ({ isOpen, onClose, candidateId, accessToken, candidateName }) => {
    const [loading, setLoading] = useState(true);
    const [photos, setPhotos] = useState([]);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && accessToken) {
            fetchPhotos();
        }
    }, [isOpen, accessToken]);

    const fetchPhotos = async () => {
        if (!accessToken) {
            console.error('❌ No access token provided');
            setError('No access token available. Please try again.');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            console.log('📸 Fetching photos for token:', accessToken.substring(0, 20) + '...');

            const response = await api.get(`/face-photos/${accessToken}/photos`);

            console.log('📸 API Response:', response.data);

            if (response.data.success) {
                const sortedPhotos = response.data.photos.sort((a, b) => a.photo_order - b.photo_order);

                // Photos already have presigned URLs from backend
                const processedPhotos = sortedPhotos.map(photo => {
                    let displayUrl = photo.photo_url || photo.display_url || '';

                    // If it's a relative local URL, prepend the base URL
                    if (displayUrl && displayUrl.startsWith('/uploads/')) {
                        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                        const baseWithoutApi = baseUrl.replace('/api', '');
                        displayUrl = `${baseWithoutApi}${displayUrl}`;
                    }

                    // If no URL, show placeholder
                    if (!displayUrl || displayUrl === '') {
                        displayUrl = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24"%3E%3Cpath fill="%23999" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4-3h2v13h-2z"/%3E%3C/svg%3E';
                    }

                    return {
                        ...photo,
                        display_url: displayUrl,
                        photo_url: displayUrl,
                        is_presigned: photo.is_presigned || false,
                        expires_at: photo.expires_at || null
                    };
                });

                console.log('📸 Processed photos:', processedPhotos.length);
                setPhotos(processedPhotos);

                if (processedPhotos.length > 0) {
                    setSelectedPhotoIndex(0);
                }
            } else {
                setError(response.data.error || 'Failed to load photos');
            }
        } catch (error) {
            console.error('❌ Error fetching photos:', error);
            setError(error.response?.data?.error || 'Failed to load photos');
        } finally {
            setLoading(false);
        }
    };

    const getPhotoLabel = (order) => {
        switch (order) {
            case 1: return 'Pre-Assessment Photo';
            case 2: return 'Mid-Assessment Photo';
            case 3: return 'Final Photo';
            default: return `Photo ${order}`;
        }
    };

    const getStorageLabel = (storageType) => {
        if (storageType === 's3') return 'Cloud Storage';
        return 'Local Storage';
    };

    const handleDownload = async (photoUrl, order, storageType) => {
        try {
            if (!photoUrl) {
                showError('No photo URL available');
                return;
            }

            // For presigned URLs or S3 URLs, open in new tab
            if (storageType === 's3' || photoUrl.includes('amazonaws.com') || photoUrl.includes('X-Amz')) {
                window.open(photoUrl, '_blank');
                showSuccess(`Photo ${order} opened`);
                return;
            }

            // For local URLs, fetch and download
            if (photoUrl.includes('/uploads/')) {
                const response = await fetch(photoUrl);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `photo_${order}.jpg`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                showSuccess(`Photo ${order} downloaded`);
            } else {
                window.open(photoUrl, '_blank');
            }
        } catch (error) {
            console.error('Error downloading photo:', error);
            showError('Failed to download photo');
        }
    };

    const goToPrevious = () => {
        if (selectedPhotoIndex > 0) {
            setSelectedPhotoIndex(selectedPhotoIndex - 1);
        }
    };

    const goToNext = () => {
        if (selectedPhotoIndex < photos.length - 1) {
            setSelectedPhotoIndex(selectedPhotoIndex + 1);
        }
    };

    if (!isOpen) return null;

    const currentPhoto = photos[selectedPhotoIndex] || null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center">
                        <CameraIcon className="h-6 w-6 text-[#063b56] mr-2" />
                        <h3 className="text-lg font-bold text-[#063b56]">Captured Photos</h3>
                        {candidateName && (
                            <span className="ml-2 text-sm text-gray-500">
                                - {candidateName}
                            </span>
                        )}
                        <span className="ml-2 text-sm text-gray-500">
                            ({photos.length} of 3)
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <XIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff9f2f] mx-auto"></div>
                                <p className="mt-4 text-gray-600">Loading photos...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="text-center text-red-600">
                                <p>{error}</p>
                                <button
                                    onClick={fetchPhotos}
                                    className="mt-4 px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a]"
                                >
                                    Retry
                                </button>
                            </div>
                        </div>
                    ) : photos.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-64 text-center">
                            <CameraIcon className="h-16 w-16 text-gray-300 mb-4" />
                            <h3 className="text-lg font-medium text-gray-700">No Photos Captured</h3>
                            <p className="text-gray-500 text-sm mt-2">
                                Photos will be captured during the assessment:
                            </p>
                            <ul className="text-sm text-gray-500 mt-2 space-y-1">
                                <li>• Photo 1: Before starting the assessment</li>
                                <li>• Photo 2: Middle of the assessment</li>
                                <li>• Photo 3: At the end of the assessment</li>
                            </ul>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Photo Status Summary */}
                            {/* <div className="grid grid-cols-3 gap-2">
                                {[1, 2, 3].map((order) => {
                                    const photo = photos.find(p => p.photo_order === order);
                                    return (
                                        <div
                                            key={order}
                                            className={`p-2 rounded-lg text-center text-sm ${photo
                                                    ? 'bg-green-50 border border-green-200 text-green-700'
                                                    : 'bg-gray-50 border border-gray-200 text-gray-500'
                                                }`}
                                        >
                                            <div className="font-medium">Photo {order}</div>
                                            <div className="text-xs">
                                                {photo ? '✅ Captured' : '❌ Not Captured'}
                                            </div>
                                            {photo && photo.storage_type && (
                                                <div className="text-xs text-gray-400 mt-1">
                                                    {getStorageLabel(photo.storage_type)}
                                                </div>
                                            )}
                                            {photo && photo.is_presigned && (
                                                <div className="text-xs text-blue-400 mt-1">
                                                    🔒 Presigned URL
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div> */}

                            {/* Photo Viewer */}
                            {currentPhoto && (
                                <div className="bg-gray-100 rounded-lg overflow-hidden">
                                    <div className="relative">
                                        <img
                                            src={currentPhoto.display_url}
                                            alt={`Photo ${currentPhoto.photo_order}`}
                                            className="w-full max-h-[400px] object-contain"
                                            onError={(e) => {
                                                console.error('❌ Image failed to load:', e.target.src);
                                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24"%3E%3Cpath fill="%23999" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4-3h2v13h-2z"/%3E%3C/svg%3E';
                                                e.target.className = 'w-full h-64 object-contain';
                                            }}
                                        />

                                        {/* Navigation Arrows */}
                                        {photos.length > 1 && (
                                            <>
                                                <button
                                                    onClick={goToPrevious}
                                                    disabled={selectedPhotoIndex === 0}
                                                    className={`absolute left-2 top-1/2 transform -translate-y-1/2 
                                                        p-2 rounded-full bg-black/50 text-white hover:bg-black/70 
                                                        transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}
                                                >
                                                    <ChevronLeftIcon className="h-6 w-6" />
                                                </button>
                                                <button
                                                    onClick={goToNext}
                                                    disabled={selectedPhotoIndex === photos.length - 1}
                                                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 
                                                        p-2 rounded-full bg-black/50 text-white hover:bg-black/70 
                                                        transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}
                                                >
                                                    <ChevronRightIcon className="h-6 w-6" />
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {/* Photo Info */}
                                    <div className="p-4 bg-white">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <h4 className="font-medium text-gray-800">
                                                    {getPhotoLabel(currentPhoto.photo_order)}
                                                </h4>
                                                <p className="text-sm text-gray-500">
                                                    {currentPhoto.photo_timestamp
                                                        ? `Captured: ${new Date(currentPhoto.photo_timestamp).toLocaleString()}`
                                                        : 'Capture time: N/A'
                                                    }
                                                </p>
                                                {currentPhoto.storage_type && (
                                                    <p className="text-xs text-gray-400">
                                                        Storage: {getStorageLabel(currentPhoto.storage_type)}
                                                    </p>
                                                )}
                                                {currentPhoto.is_presigned && currentPhoto.expires_at && (
                                                    <p className="text-xs text-gray-400">
                                                        Expires: {new Date(currentPhoto.expires_at).toLocaleString()}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleDownload(
                                                    currentPhoto.photo_url,
                                                    currentPhoto.photo_order,
                                                    currentPhoto.storage_type
                                                )}
                                                className="px-3 py-1.5 bg-[#063b56] text-white text-sm rounded-lg hover:bg-[#0a4a6a] flex items-center"
                                            >
                                                <DownloadIcon className="h-4 w-4 mr-1" />
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Photo Thumbnails */}
                            {photos.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {photos.map((photo, index) => (
                                        <button
                                            key={photo.id}
                                            onClick={() => setSelectedPhotoIndex(index)}
                                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${index === selectedPhotoIndex
                                                ? 'border-[#ff9f2f] shadow-md'
                                                : 'border-gray-200 hover:border-gray-400'
                                                }`}
                                        >
                                            <img
                                                src={photo.display_url}
                                                alt={`Photo ${photo.photo_order}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24"%3E%3Cpath fill="%23999" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4-3h2v13h-2z"/%3E%3C/svg%3E';
                                                }}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <p className="text-xs text-gray-500">
                            {photos.length > 0
                                ? `${photos.length} of 3 photos captured`
                                : 'No photos captured yet'
                            }
                        </p>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PhotoModal;